import {useStyles} from "@/components/call-group/members/styles";
import {FormattedMessage} from "react-intl";
import {ActivationTime} from "@/api";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Grid, IconButton, Table, TableBody, TableCell, TableRow} from "@mui/material";
import {groupActivationDays, sortActivationDays, sortActivationTimes} from "@/tools";
import {TimePicker} from "@dvrt/graphql-widgets";
import {WEEKDAY_MESSAGES} from "@/utils/translations";

type UpdateActivationSettings = {
    activationTimes: any[];
};

type Props = {
    activationTimes: ActivationTime[];
    disabled: boolean;
    updater: (settings: UpdateActivationSettings) => void;
    uniqueValidator: (newActivationTimes: ActivationTime[]) => void;
};

export const ActivationTimes = ({activationTimes, disabled, updater, uniqueValidator}: Props) => {
    const {classes, cx} = useStyles();

    function handleAddActivationTime(groupIndex: number, time: string) {
        let currentTimes = activationTimes.map((time) => structuredClone(time));

        if (!currentTimes[groupIndex].times.includes(time)) {
            currentTimes[groupIndex].times.push(time);
            currentTimes[groupIndex] = sortActivationTimes(currentTimes[groupIndex]);
        }

        currentTimes = groupActivationDays(currentTimes);

        updater({activationTimes: currentTimes});
        uniqueValidator(currentTimes);
    }

    function handleRemoveActivationTime(groupIndex: number, timeIndex: number) {
        let currentTimes = activationTimes.map((time) => structuredClone(time));

        currentTimes[groupIndex].times.splice(timeIndex, 1);

        currentTimes[groupIndex] = sortActivationTimes(currentTimes[groupIndex]);

        currentTimes = groupActivationDays(currentTimes);

        updater({activationTimes: currentTimes});
        uniqueValidator(currentTimes);
    }

    function handleSeparateActivationTime(groupIndex: number, dayIndex: number) {
        let currentTimes = activationTimes.map((time) => structuredClone(time));

        currentTimes.splice(groupIndex + 1, 0, {
            weekdays: [currentTimes[groupIndex].weekdays[dayIndex]],
            times: currentTimes[groupIndex].times,
        });
        currentTimes[groupIndex].weekdays.splice(dayIndex, 1);

        currentTimes = sortActivationDays(currentTimes);

        updater({activationTimes: currentTimes});
    }

    return (
        <Table style={{paddingTop: 3}} size={"small"}>
            <TableBody>
                {activationTimes.map((group, groupIndex) => (
                    <TableRow className={cx(classes.row)} key={groupIndex}>
                        <TableCell>
                            {group.weekdays.map((day, dayIndex) => [
                                dayIndex > 0 && ", ",
                                <FormattedMessage {...WEEKDAY_MESSAGES[day]} key={`msg-${dayIndex}`} />,
                                group.weekdays.length > 1 && (
                                    <IconButton
                                        key={`icon-${dayIndex}`}
                                        onClick={() => handleSeparateActivationTime(groupIndex, dayIndex)}
                                        color="secondary"
                                        size="small"
                                        style={{verticalAlign: "bottom"}}
                                    >
                                        <FontAwesomeIcon icon="minus" />
                                    </IconButton>
                                ),
                            ])}
                        </TableCell>
                        <TableCell>
                            <Grid container>
                                <Grid size={11}>
                                    {group.times.map((time, timeIndex) => [
                                        timeIndex > 0 && ", ",
                                        <span key={`msg-${timeIndex}`}>{time}</span>,
                                        <IconButton
                                            key={`icon-${timeIndex}`}
                                            onClick={() => handleRemoveActivationTime(groupIndex, timeIndex)}
                                            color="secondary"
                                            size="small"
                                            style={{verticalAlign: "bottom"}}
                                        >
                                            <FontAwesomeIcon icon="minus" />
                                        </IconButton>,
                                    ])}
                                </Grid>
                                <Grid size={1}>
                                    <TimePicker
                                        value={"00:00"}
                                        onChange={(v) => handleAddActivationTime(groupIndex, v)}
                                        minutesStep={15}
                                        disabled={disabled}
                                    />
                                </Grid>
                            </Grid>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};
